import React, { useCallback, useEffect, useMemo, useRef, useState, useContext, createContext } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { formatRut, rutWithNumbers, rutWithDash } from "../../utils/formatter";
import { CompanyContext, UserContext } from "src/context";
import {
  importSelectedCompany,
  importLoadingState,
} from "../../redux/actions/companyList";
import { importOpenSidebar } from "src/redux/actions/openSidebar";
import { Plans } from "src/utils/plans";
import { Roles } from "src/utils/roles";
import "./CompanyListComponent.scss";
import CreateCompanyModal from "./CreateCompanyModal";

// Estado vivo del checklist: evita reconstruir `options` del Dropdown en cada toggle
// (ese rebuild hacía que Semantic UI cerrara/reabriera el menú y el checkbox “fallara”).
const GroupChecklistContext = createContext(null);

const InlineGroupChecklistSection = () => {
  const ctx = useContext(GroupChecklistContext);
  const [optimisticSelected, setOptimisticSelected] = useState(null);

  useEffect(() => {
    setOptimisticSelected(null);
  }, [ctx?.selectedValuesKey]);

  if (!ctx) {
    return null;
  }

  const {
    isOpen,
    options,
    lockedValues,
    selectedValues,
    onToggle,
    onSelectAll,
    suppressDropdownCloseBriefly,
  } = ctx;

  if (!isOpen) {
    return (
      <div className="company-group-inline-section company-group-inline-section--closed" />
    );
  }

  // `[]` es truthy: no usar `optimistic || selected` o un array vacío pisa Redux.
  const effectiveSelected = (
    optimisticSelected != null ? optimisticSelected : selectedValues || []
  ).map(String);
  const lockedSet = new Set((lockedValues || []).map(String));
  const allSelected =
    options.length > 0 && options.every((opt) => effectiveSelected.includes(String(opt.value)));

  // Semantic UI cierra el menú en mousedown del .item; hay que bloquearlo ANTES
  // del onChange del checkbox y marcar suppress para el onClose controlado.
  const holdDropdownOpen = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    suppressDropdownCloseBriefly?.();
  };

  const stopMenuEvent = (e) => {
    e?.stopPropagation?.();
  };

  const applyToggle = (optionValue) => {
    const normalized = String(optionValue);
    if (lockedSet.has(normalized)) return;

    const selectedSet = new Set(effectiveSelected);
    if (selectedSet.has(normalized)) {
      selectedSet.delete(normalized);
    } else {
      selectedSet.add(normalized);
    }
    lockedSet.forEach((id) => selectedSet.add(id));
    const next = Array.from(selectedSet);
    setOptimisticSelected(next);
    onToggle?.(next);
  };

  const applySelectAll = () => {
    const allOptionIds = options.map((option) => String(option.value));
    const next = allSelected
      ? Array.from(lockedSet)
      : [...new Set([...allOptionIds, ...lockedSet])];
    setOptimisticSelected(next);
    onSelectAll?.(next);
  };

  // Toggle en click: el preventDefault del mousedown anula el change nativo.
  const handleToggleClick = (optionValue, e) => {
    holdDropdownOpen(e);
    applyToggle(optionValue);
  };

  const handleSelectAllClick = (e) => {
    holdDropdownOpen(e);
    applySelectAll();
  };

  return (
    <div
      className="company-group-inline-section company-group-inline-section--open"
      onMouseDown={holdDropdownOpen}
      onClick={stopMenuEvent}
    >
      <div className="company-group-inline-section__header">Agrupar grupo empresarial</div>

      <div className="company-group-inline-section__option company-group-inline-section__option--all">
        <label
          className="company-group-inline-section__checkbox"
          onMouseDown={holdDropdownOpen}
          onClick={handleSelectAllClick}
        >
          <input
            type="checkbox"
            checked={allSelected}
            readOnly
            tabIndex={-1}
            onMouseDown={holdDropdownOpen}
          />
          <span className="company-group-inline-section__checkbox-label">Seleccionar todo</span>
        </label>
      </div>

      <div className="company-group-inline-section__divider"></div>

      <div className="company-group-inline-section__list">
        {options.length > 0 ? (
          options.map((option) => {
            const optionValue = String(option.value);
            const isChecked = effectiveSelected.includes(optionValue);
            const isLocked = lockedSet.has(optionValue);
            return (
              <div
                key={optionValue}
                className="company-group-inline-section__option"
                onMouseDown={holdDropdownOpen}
                onClick={stopMenuEvent}
              >
                <label
                  className="company-group-inline-section__checkbox"
                  onMouseDown={holdDropdownOpen}
                  onClick={isLocked ? holdDropdownOpen : (e) => handleToggleClick(optionValue, e)}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isLocked}
                    readOnly
                    tabIndex={-1}
                    onMouseDown={holdDropdownOpen}
                  />
                  <span className="company-group-inline-section__checkbox-label">{option.label}</span>
                </label>
              </div>
            );
          })
        ) : (
          <div className="company-group-inline-section__no-results">No se encontraron resultados</div>
        )}
      </div>
    </div>
  );
};

const formatCompanyRut = (rut) => (rutWithDash(rut) || "").toUpperCase();
const COMPANY_MENU_TOP_GAP_PX = 2;
const COMPANY_MENU_SIDEBAR_GAP_PX = 10;
const COMPANY_MENU_BOTTOM_INSET_PX = 100;
const COMPANY_MENU_MIN_WIDTH_PX = 280;
const COMPANY_MENU_MAX_WIDTH_PX = 624;
const COMPANY_MENU_VIEWPORT_INSET_PX = 32;

const getCompanyMenuMaxHeight = (triggerBottom) =>
  Math.max(
    200,
    Math.floor(window.innerHeight - triggerBottom - COMPANY_MENU_TOP_GAP_PX - COMPANY_MENU_BOTTOM_INSET_PX),
  );

const getCompanyMenuWidth = () =>
  Math.min(
    COMPANY_MENU_MAX_WIDTH_PX,
    Math.max(COMPANY_MENU_MIN_WIDTH_PX, window.innerWidth - COMPANY_MENU_VIEWPORT_INSET_PX),
  );

const MENU_INLINE_POSITION_PROPS = [
  "position",
  "left",
  "top",
  "right",
  "width",
  "min-width",
  "max-width",
  "transform",
  "z-index",
];

const applyDropdownMenuStyles = (dropdownMenu, dropdownHost, dropdownTrigger) => {
  if (!dropdownMenu || !dropdownHost || !dropdownTrigger) return;
  const hostRect = dropdownHost.getBoundingClientRect();
  const triggerRect = dropdownTrigger.getBoundingClientRect();
  const menuHeight = getCompanyMenuMaxHeight(triggerRect.bottom);

  dropdownHost.style.setProperty("--company-menu-height", `${menuHeight}px`);
  dropdownHost.style.setProperty("--company-menu-width", `${getCompanyMenuWidth()}px`);
  dropdownMenu.style.removeProperty("height");
  dropdownMenu.style.removeProperty("max-height");
  MENU_INLINE_POSITION_PROPS.forEach((prop) => {
    dropdownMenu.style.removeProperty(prop);
  });
};

const buildCreateCompanyButton = (onCreateClick) => {
  const buttonContainer = document.createElement("div");
  buttonContainer.className = "create-company-button";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "create-company-button__action";
  button.textContent = "+ Crear nueva empresa";

  button.addEventListener("mousedown", (e) => {
    e.preventDefault();
    e.stopPropagation();
  });

  button.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCreateClick();
  });

  buttonContainer.appendChild(button);
  return buttonContainer;
};

const buildCombineBar = (onCombineClick) => {
  const bar = document.createElement("div");
  bar.className = "company-group-combine-bar";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "company-combine-trigger company-combine-trigger--bar";
  button.textContent = "Combinar";

  button.addEventListener("mousedown", (e) => {
    e.preventDefault();
    e.stopPropagation();
  });

  button.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCombineClick(e);
  });

  bar.appendChild(button);
  return bar;
};

const CompanyListComponent = ({
  clearUrlParams,
  enableGroupChecklist = false,
  groupChecklistOptions = [],
  groupChecklistSelectedValues = [],
  onGroupChecklistChange,
  groupChecklistLockedValues = [],
}) => {
  const { t, i18n } = useTranslation("header");
  const dispatch = useDispatch();
  const [newSelectedCompany, setNewSelectedCompany] = useState();
  const [options, setOptions] = useState([]);
  const [companyDataMap, setCompanyDataMap] = useState({}); // Store companyData separately to avoid passing to DOM
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isGroupChecklistOpen, setIsGroupChecklistOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const isGroupChecklistOpenRef = useRef(false);
  const suppressDropdownCloseRef = useRef(false);
  const dropdownRef = useRef(null);
  
  const { companyList, selectedCompany } = useContext(CompanyContext);
  const { roles } = useContext(UserContext);
  const { updateCompanyNeeded } = useSelector((state) => state.companyList);
  
  // Check if user has permission to create companies
  const canCreateCompany = roles?.includes(Roles.ADMIN) || roles?.includes(Roles.SUPER_COLABORADOR);

  const openCreateCompanyModal = useCallback(() => {
    setIsDropdownOpen(false);
    setIsCreateModalOpen(true);
  }, []);

  const setGroupChecklistOpen = useCallback((next) => {
    setIsGroupChecklistOpen((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      isGroupChecklistOpenRef.current = value;
      return value;
    });
  }, []);

  const suppressDropdownCloseBriefly = useCallback(() => {
    suppressDropdownCloseRef.current = true;
    window.setTimeout(() => {
      suppressDropdownCloseRef.current = false;
    }, 750);
  }, []);

  const closeDropdown = useCallback(() => {
    isGroupChecklistOpenRef.current = false;
    setGroupChecklistOpen(false);
    setCompanySearchQuery("");
    setIsDropdownOpen(false);
  }, [setGroupChecklistOpen]);

  const stopDropdownItemEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const openGroupChecklist = useCallback(() => {
    suppressDropdownCloseBriefly();
    setIsDropdownOpen(true);
    setGroupChecklistOpen(true);
  }, [setGroupChecklistOpen, suppressDropdownCloseBriefly]);

  const ensureCreateCompanyButtonAtBottom = useCallback(() => {
    if (!canCreateCompany) return;
    const host = dropdownRef.current;
    const dropdownMenu = host?.querySelector(".menu");
    if (!dropdownMenu) return;

    let buttonContainer = dropdownMenu.querySelector(".create-company-button");
    if (!buttonContainer) {
      buttonContainer = buildCreateCompanyButton(openCreateCompanyModal);
    }
    dropdownMenu.appendChild(buttonContainer);
  }, [canCreateCompany, openCreateCompanyModal]);

  const ensureCombineBarAtTop = useCallback(() => {
    const host = dropdownRef.current;
    const dropdownMenu = host?.querySelector(".menu");
    if (!dropdownMenu) return;

    const existingBar = dropdownMenu.querySelector(".company-group-combine-bar");
    // UX: keep only the per-company "Combinar" button; no sticky top bar.
    existingBar?.remove();
  }, [enableGroupChecklist, openGroupChecklist]);

  const scrollSelectedCompanyRowIntoView = useCallback(() => {
    const host = dropdownRef.current;
    const menu = host?.querySelector(".menu");
    if (!menu || !selectedCompany?.id) return;

    const selectedItem =
      menu.querySelector(".item.selected") ||
      menu.querySelector(`.item[data-value="${selectedCompany.id}"]`) ||
      Array.from(menu.querySelectorAll(".item")).find((item) => {
        const value = item.getAttribute("data-value");
        return value && String(value) === String(selectedCompany.id);
      });

    selectedItem?.scrollIntoView({ block: "start", behavior: "auto" });
  }, [selectedCompany?.id]);

  const syncDropdownMenuUi = useCallback((options = {}) => {
    const { scrollSelectedIntoView = false } = options;
    const host = dropdownRef.current;
    const dropdownTrigger = host?.querySelector(".company-selector-dropdown");
    const dropdownMenu = host?.querySelector(".menu");
    if (!host || !dropdownMenu || !dropdownTrigger) return;
    applyDropdownMenuStyles(dropdownMenu, host, dropdownTrigger);
    ensureCombineBarAtTop();
    ensureCreateCompanyButtonAtBottom();
    if (scrollSelectedIntoView) {
      scrollSelectedCompanyRowIntoView();
    }
  }, [
    ensureCombineBarAtTop,
    ensureCreateCompanyButtonAtBottom,
    scrollSelectedCompanyRowIntoView,
  ]);

  const scheduleSyncDropdownMenuUi = useCallback((options = {}) => {
    window.setTimeout(() => syncDropdownMenuUi(options), 0);
  }, [syncDropdownMenuUi]);

  const handleCompanySearchChange = useCallback(
    (e, { searchQuery }) => {
      setCompanySearchQuery(searchQuery || "");
      scheduleSyncDropdownMenuUi();
    },
    [scheduleSyncDropdownMenuUi],
  );

  useEffect(() => {
    if (!isDropdownOpen) {
      setCompanySearchQuery("");
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const handleResize = () => scheduleSyncDropdownMenuUi();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isDropdownOpen, scheduleSyncDropdownMenuUi]);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;

    const handleOutsidePointerDown = (event) => {
      const host = dropdownRef.current;
      if (!host || !(event.target instanceof Node)) return;
      if (host.contains(event.target)) return;
      if (suppressDropdownCloseRef.current) return;
      closeDropdown();
    };

    document.addEventListener("mousedown", handleOutsidePointerDown);
    return () => document.removeEventListener("mousedown", handleOutsidePointerDown);
  }, [isDropdownOpen, closeDropdown]);

  const normalizedLockedValues = useMemo(
    () => (Array.isArray(groupChecklistLockedValues) ? [...new Set(groupChecklistLockedValues.map(String))] : []),
    [groupChecklistLockedValues]
  );

  const normalizedSelectedValues = useMemo(() => {
    const base = Array.isArray(groupChecklistSelectedValues)
      ? groupChecklistSelectedValues.map(String)
      : [];
    return [...new Set([...base, ...normalizedLockedValues])];
  }, [groupChecklistSelectedValues, normalizedLockedValues]);

  const normalizedGroupChecklistOptions = useMemo(
    () => (Array.isArray(groupChecklistOptions) ? groupChecklistOptions : []),
    [groupChecklistOptions]
  );

  const applyGroupChecklistSelection = useCallback(
    (nextValues) => {
      if (!onGroupChecklistChange) return;
      onGroupChecklistChange(nextValues);
    },
    [onGroupChecklistChange]
  );

  const groupChecklistContextValue = useMemo(
    () => ({
      isOpen: isGroupChecklistOpen,
      options: normalizedGroupChecklistOptions,
      lockedValues: normalizedLockedValues,
      selectedValues: normalizedSelectedValues,
      selectedValuesKey: normalizedSelectedValues.slice().sort().join("|"),
      onToggle: applyGroupChecklistSelection,
      onSelectAll: applyGroupChecklistSelection,
      suppressDropdownCloseBriefly,
    }),
    [
      isGroupChecklistOpen,
      normalizedGroupChecklistOptions,
      normalizedLockedValues,
      normalizedSelectedValues,
      applyGroupChecklistSelection,
      suppressDropdownCloseBriefly,
    ]
  );

  useEffect(() => {
    if (!isDropdownOpen) return;
    scheduleSyncDropdownMenuUi({ scrollSelectedIntoView: isGroupChecklistOpen });
  }, [isGroupChecklistOpen, isDropdownOpen, scheduleSyncDropdownMenuUi]);

  useEffect(() => {
    if (newSelectedCompany) {
      dispatch(
        importOpenSidebar({
          show: false,
        })
      );
      dispatch(importSelectedCompany(newSelectedCompany, { userInitiated: true }));
      dispatch(importLoadingState({ load: true }));
      clearUrlParams();
    }
  }, [newSelectedCompany, dispatch]);

  useEffect(() => {
    document.querySelectorAll(".create-company-button button[data-i18n-key]").forEach((btn) => {
      btn.textContent = t("company.createNew");
    });
  }, [i18n.language, t]);

  useEffect(() => {
    const List =
      companyList?.length > 0
        ? companyList?.map((element, index) => {
          const showBlock = element?.plan
            ? element?.plan.includes(Plans.blocked)
            : false;

          const companyNumber = element?.correlative || "—";
          const companyName = element?.alias || element?.name || "";
          const companyRut = formatCompanyRut(element?.rut) || "";
          const isSelectedCompanyRow =
            String(selectedCompany?.id || "") === String(element?.id || "");
          const isGroupTriggerRow = enableGroupChecklist && isSelectedCompanyRow;
          const isCompanyCombinable = enableGroupChecklist && isSelectedCompanyRow;

          const handleGroupTrigger = (e) => {
            if (!isGroupTriggerRow) return;
            stopDropdownItemEvent(e);
            suppressDropdownCloseBriefly();
            setIsDropdownOpen(true);
            setGroupChecklistOpen((prev) => !prev);
          };

          const handleCombineTrigger = (e) => {
            if (!isGroupTriggerRow) return;
            stopDropdownItemEvent(e);
            suppressDropdownCloseBriefly();
            setIsDropdownOpen(true);
            setGroupChecklistOpen((prev) => !prev);
          };

          return {
            key: element?.id,
            value: element?.id,
            text: showBlock
              ? `${companyName} (${companyNumber}) ${t("company.blocked")}`
              : `${companyName} (${companyNumber})`,
            content: (
              <div
                className="company-dropdown-item"
                onMouseDown={(e) => {
                  if (!isGroupTriggerRow) return;
                  if (e.target.closest(".company-number--group-trigger")) return;
                  if (e.target.closest(".company-combine-trigger")) return;
                  // Checklist / fila activa: no dejar que Semantic UI tome el item.
                  e.preventDefault();
                  e.stopPropagation();
                  suppressDropdownCloseBriefly();
                }}
                onClick={(e) => {
                  if (!isGroupTriggerRow) return;
                  if (e.target.closest(".company-number--group-trigger")) return;
                  if (e.target.closest(".company-combine-trigger")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  suppressDropdownCloseBriefly();
                }}
              >
                <div className="company-dropdown-item-main">
                  <div
                    className={`company-number ${isGroupTriggerRow ? "company-number--group-trigger" : ""}`}
                    title={isGroupTriggerRow ? "Agrupar por grupo empresarial" : undefined}
                    onMouseDown={handleGroupTrigger}
                    onClick={stopDropdownItemEvent}
                  >
                    {companyNumber}.
                  </div>
                  <div className={`company-info ${isCompanyCombinable ? "company-info--with-combine" : ""}`}>
                    <span className="company-name">{companyName}</span>
                    {showBlock && <span className="blocked-tag">(Bloqueado)</span>}
                    {isCompanyCombinable && (
                      <button
                        type="button"
                        className="company-combine-trigger"
                        onMouseDown={handleCombineTrigger}
                        onClick={stopDropdownItemEvent}
                      >
                        Combinar
                      </button>
                    )}
                  </div>
                  <div className="company-rut">{companyRut}</div>
                </div>

                {enableGroupChecklist && isSelectedCompanyRow && <InlineGroupChecklistSection />}
              </div>
            ),
          };
        })
        : [];

    // Build companyDataMap for search functionality
    const dataMap = {};
    companyList?.forEach((element) => {
      if (element?.id) {
        dataMap[element.id] = {
          id: element?.id,
          name: element?.name,
          alias: element?.alias,
          plan: element?.plan,
          trialExpirationDate: element?.trialExpirationDate,
          rut: element?.rut,
          users: element?.users,
          suscriptionId: element?.suscriptionId,
        };
      }
    });
    setCompanyDataMap(dataMap);
    setOptions(List);
  }, [
    companyList,
    updateCompanyNeeded?.value,
    enableGroupChecklist,
    selectedCompany?.id,
    suppressDropdownCloseBriefly,
    setGroupChecklistOpen,
    t,
  ]);
  const customSearch = (options, query) => {
    return options.filter((option) => {
      const company = companyDataMap[option.value];
      if (!company) return false;
      
      if (company?.alias?.toUpperCase()?.includes(query?.toUpperCase()))
        return option;
      if (company?.rut?.toUpperCase()?.includes(query?.toUpperCase()))
        return option;
      if (
        formatRut(company?.rut?.toUpperCase())?.includes(
          query?.toUpperCase()
        )
      )
        return option;
      if (
        rutWithNumbers(company?.rut?.toUpperCase())?.includes(
          query?.toUpperCase()
        )
      )
        return option;
      if (company?.name?.toUpperCase()?.includes(query?.toUpperCase()))
        return option;
    });
  };

  const selectedCompanyTrigger = selectedCompany ? (
    <div className="company-selector-trigger-content">
      <span className="company-selector-trigger-content__name">
        {selectedCompany.alias || selectedCompany.name}
      </span>
      <span className="company-selector-trigger-content__rut">
        {formatCompanyRut(selectedCompany.rut)}
      </span>
    </div>
  ) : (
    <span className="company-selector-trigger-content__placeholder">Seleccionar empresa</span>
  );

  return (
    <GroupChecklistContext.Provider value={groupChecklistContextValue}>
      <div
        ref={dropdownRef}
        className={`company-selector-dropdown-host ${isDropdownOpen ? "company-selector-dropdown--open" : ""} ${companySearchQuery ? "company-selector-dropdown--searching" : ""}`}
      >
      <Dropdown
        placeholder="Seleccionar empresa"
        trigger={selectedCompanyTrigger}
        text={
          selectedCompany
            ? `${selectedCompany.alias || selectedCompany.name} ${formatCompanyRut(selectedCompany.rut)}`
            : "Seleccionar empresa"
        }
        fluid
        search={customSearch}
        searchQuery={companySearchQuery}
        onSearchChange={handleCompanySearchChange}
        selection
        options={options}
        value={selectedCompany?.id}
        onChange={(e, { value }) => {
          const selectedCompanyData = companyList.find(company => company.id === value);
          // Prevent setting the same company multiple times
          if (selectedCompanyData && selectedCompanyData.id !== selectedCompany?.id) {
            setNewSelectedCompany(selectedCompanyData);
            setIsDropdownOpen(false);
          }
        }}
        selectOnBlur={false}
        open={isDropdownOpen}
        loading={options.length === 0}
        disabled={options.length === 0}
        noResultsMessage="No se encontraron empresas"
        style={{
          width: "100%",
          maxWidth: "100%",
          borderRadius: "8px"
        }}
        className="company-selector-dropdown"
        onClose={() => {
          // Al interactuar con el checklist Semantic UI dispara onClose; reabrir.
          if (suppressDropdownCloseRef.current) {
            setIsDropdownOpen(true);
            return;
          }
          closeDropdown();
        }}
        onOpen={() => {
          setIsDropdownOpen(true);
          // No cerrar el checklist si el "open" viene de un re-assert tras un toggle.
          if (!suppressDropdownCloseRef.current && !isGroupChecklistOpenRef.current) {
            setGroupChecklistOpen(false);
          }
          scheduleSyncDropdownMenuUi({ scrollSelectedIntoView: true });
          window.requestAnimationFrame(() => {
            scheduleSyncDropdownMenuUi({ scrollSelectedIntoView: true });
          });
        }}
      />
      </div>
      
      {canCreateCompany && (
        <CreateCompanyModal 
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </GroupChecklistContext.Provider>
  );
};

export default CompanyListComponent;
